import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, BellRinging, Lightning } from "@phosphor-icons/react";

export default function Reminders() {
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [sampleResult, setSampleResult] = useState(null);

  const load = async () => {
    const r = await api.get("/reminders");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const run = async (dry) => {
    setRunning(true);
    try {
      const r = await api.post("/reminders/run", null, { params: { dry_run: dry } });
      toast.success(`${dry ? "Dry-run" : "Run"}: ${r.data.count} reminders ${dry ? "would fire" : "processed"}`);
      if (!dry) await load();
    } catch { toast.error("Failed"); } finally { setRunning(false); }
  };

  const sendSample = async () => {
    setSampling(true);
    setSampleResult(null);
    try {
      const r = await api.post("/reminders/sample-alert");
      setSampleResult(r.data);
      const sms = (r.data.sms_results || []).filter(x => x.status === "sent").length;
      const em = (r.data.email_results || []).filter(x => x.status === "sent").length;
      toast.success(`Sample sent · ${sms} SMS, ${em} email delivered`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send sample");
    } finally {
      setSampling(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Notification engine</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Reminders</h1>
          <p className="text-sm text-gray-600 mt-2">
            Scans all credentials against your reminder thresholds and dispatches SMS (Twilio) + email (Resend).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendSample} disabled={sampling} className="border border-amber-400 bg-amber-50 text-amber-900 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-amber-100 inline-flex items-center gap-2 disabled:opacity-60" data-testid="send-sample-btn">
            <Lightning size={14} weight="bold" /> {sampling ? "Sending…" : "Send sample to me"}
          </button>
          <button onClick={() => run(true)} disabled={running} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 disabled:opacity-60" data-testid="dry-run-btn">
            Dry run
          </button>
          <button onClick={() => run(false)} disabled={running} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2 disabled:opacity-60" data-testid="run-reminders-btn">
            <PaperPlaneRight size={14} weight="bold" /> {running ? "Running…" : "Run now"}
          </button>
        </div>
      </header>

      {sampleResult && (
        <div className="bg-amber-50 border border-amber-300 p-5" data-testid="sample-result-panel">
          <div className="overline text-amber-900">Sample alert preview</div>
          <div className="font-mono text-xs text-slate-800 mt-2 whitespace-pre-wrap break-words bg-white border border-amber-200 p-3" data-testid="sample-preview-message">
            {sampleResult.preview_message}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-xs">
            <div>
              <div className="font-semibold text-slate-700 mb-1">SMS dispatch ({sampleResult.phones?.length || 0} number{(sampleResult.phones?.length||0) === 1 ? "" : "s"})</div>
              {(sampleResult.sms_results || []).length === 0 && <div className="text-gray-500">No SMS recipients configured.</div>}
              <ul className="space-y-1">
                {(sampleResult.sms_results || []).map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 ${r.status === "sent" ? "bg-emerald-100 text-emerald-800" : r.status === "skipped" ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-800"}`}>{r.status}</span>
                    <span className="font-mono text-[11px] text-slate-700">{r.to}</span>
                    {r.error && <span className="text-red-700 text-[11px]">· {r.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-slate-700 mb-1">Email dispatch ({sampleResult.emails?.length || 0})</div>
              {(sampleResult.email_results || []).length === 0 && <div className="text-gray-500">No email recipients configured.</div>}
              <ul className="space-y-1">
                {(sampleResult.email_results || []).map((r, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 ${r.status === "sent" ? "bg-emerald-100 text-emerald-800" : r.status === "skipped" ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-800"}`}>{r.status}</span>
                    <span className="font-mono text-[11px] text-slate-700">{r.to}</span>
                    {r.error && <span className="text-red-700 text-[11px]">· {r.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-[11px] text-amber-900 mt-3">This preview was not written to the reminder log.</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="reminders-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Sent at</th>
              <th className="px-4 py-3 font-semibold">Practitioner</th>
              <th className="px-4 py-3 font-semibold">Message</th>
              <th className="px-4 py-3 font-semibold">SMS</th>
              <th className="px-4 py-3 font-semibold">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="5" className="p-12 text-center text-gray-500 text-sm">
                <BellRinging size={32} weight="duotone" className="mx-auto text-slate-400 mb-3" />
                No reminders dispatched yet. Click <span className="font-semibold">Run now</span> to scan &amp; send.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="data-row border-b border-gray-100 align-top">
                <td className="px-6 py-3 font-mono text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-medium">{r.practitioner ? `${r.practitioner.last_name}, ${r.practitioner.first_name}` : "—"}</td>
                <td className="px-4 py-3 text-gray-700 text-xs max-w-md truncate">{r.message}</td>
                <td className="px-4 py-3 text-xs font-mono">{renderChannel(r.channel_sms)}</td>
                <td className="px-4 py-3 text-xs font-mono">{renderChannel(r.channel_email)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderChannel(ch) {
  if (!ch) return "—";
  // legacy single-result object
  if (!Array.isArray(ch)) return ch.status || "—";
  if (ch.length === 0) return "—";
  return (
    <div className="space-y-1">
      {ch.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`px-1.5 py-0.5 ${r.status === "sent" ? "bg-emerald-100 text-emerald-800" : r.status === "skipped" ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-800"}`}>{r.status}</span>
          <span className="text-[10px] text-gray-500">{r.recipient_type === "practitioner" ? "→ practitioner" : "→ coordinator"}</span>
        </div>
      ))}
    </div>
  );
}
